import React from 'react';
import { Box, Typography, CircularProgress, Alert, Grid } from '@mui/material';
import type { DynamicCase } from '../../types';
import { CaseCard } from './CaseCard';

interface CaseGridProps {
  cases: DynamicCase[];
  loading: boolean;
  error: any;
  onEditCase: (caseItem: DynamicCase) => void;
  onDeleteCase: (caseItem: DynamicCase) => void;
  selectedFieldId: string;
  onSemanticReview?: (caseItem: DynamicCase) => void;
}

export const CaseGrid: React.FC<CaseGridProps> = ({
  cases,
  loading,
  error,
  onEditCase,
  onDeleteCase,
  selectedFieldId,
  onSemanticReview
}) => {
  if (loading) {
    return (
      <Box className="loading-container">
        <CircularProgress className="loading-spinner" />
        <Typography variant="body1" sx={{ color: 'text.secondary' }}>
          Loading cases...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        Failed to load cases: {error.message || 'Unknown error'}
      </Alert>
    );
  }

  if (cases.length === 0) {
    return (
      <Box 
        className="empty-state" 
        sx={{ 
          backgroundColor: 'background.paper',
          p: 4,
          textAlign: 'center',
          borderRadius: 2,
          border: '2px dashed',
          borderColor: 'divider'
        }}
      >
        <Typography variant="h5" sx={{ color: 'text.secondary', mb: 2 }}>
          No Cases Yet
        </Typography>
        <Typography variant="body1" sx={{ color: 'text.secondary', mb: 3 }}>
          This field doesn't have any cases yet. Click "Add Case" to create your first case using AI processing.
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          💡 You can process raw case text with LLM extraction to automatically populate case details.
        </Typography>
      </Box>
    );
  }

  return (
    <Grid container spacing={3} alignItems="stretch">
      {cases.map((caseItem) => (
        <Grid 
          size={{xs: 12, sm: 12, md: 6}}
          key={caseItem.id}
        >
          <CaseCard
            case={caseItem}
            onEdit={() => onEditCase(caseItem)}
            onDelete={() => onDeleteCase(caseItem)}
            onSemanticReview={onSemanticReview ? () => onSemanticReview(caseItem) : undefined}
          />
        </Grid>
      ))}
    </Grid>
  );
};